import React, { useState, useEffect, useRef } from "react"
import { CaretDown, Check } from "@phosphor-icons/react"
import { useDivisions } from "../../hooks"
import { useUIStore } from "../../stores"
import { cn } from "../../lib/utils"
import type { Division } from "../../types"

// Default division colors for visual distinction
const divisionColors: Record<string, string> = {
  Bankruptcy: "#2CC4E4",
  "Personal Injury": "#2dce89",
  "Family Law": "#fb6340",
  Immigration: "#8b5cf6",
}

function getDivisionColor(division: Division | null): string {
  if (!division) return "#9ca3af" // gray for "All Divisions"
  return division.color || divisionColors[division.name] || "#2CC4E4"
}

export function DivisionSelector() {
  const [isOpen, setIsOpen] = useState(false)
  const { data: divisions = [], isLoading } = useDivisions()
  const { selectedDivision, setSelectedDivision } = useUIStore()
  const menuRef = useRef<HTMLDivElement>(null)

  const currentDivision = divisions.find((d) => d._id === selectedDivision) || null
  const displayName = currentDivision?.name || "All Divisions"
  const divisionColor = getDivisionColor(currentDivision)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (divisionId: string | null) => {
    setSelectedDivision(divisionId)
    setIsOpen(false)
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={cn(
          "flex items-center gap-2.5 h-9 pl-3 pr-2.5 rounded-lg",
          "bg-background border border-border",
          "text-sm font-medium text-foreground",
          "transition-all duration-200",
          "hover:border-border hover:shadow-sm",
          "max-w-[180px] lg:max-w-none",
          isOpen && "bg-muted border-border shadow-sm",
          isLoading && "opacity-50 cursor-wait"
        )}
      >
        {/* Color dot with subtle glow */}
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full transition-all duration-200"
          style={{
            backgroundColor: divisionColor,
            boxShadow: `0 0 0 2px ${divisionColor}20`,
          }}
        />
        <span className="text-foreground truncate">{displayName}</span>
        <CaretDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 top-full mt-2 w-56 max-w-[calc(100vw-2rem)] rounded-xl",
            "bg-background border border-border shadow-xl",
            "z-50 overflow-hidden",
            "animate-flyout-enter"
          )}
        >
          {/* Header */}
          <div className="px-3 py-2.5 bg-gradient-to-br from-muted to-background border-b border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Workspace
            </p>
          </div>

          <div className="py-1">
            {/* All Divisions option */}
            <button
              onClick={() => handleSelect(null)}
              className={cn(
                "flex w-full items-center gap-3 px-3 py-2.5 text-sm",
                "transition-colors duration-100",
                "hover:bg-muted",
                !selectedDivision && "bg-primary/5"
              )}
            >
              <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground" />
              <div className="flex-1 text-left">
                <span className="text-foreground font-medium">All Divisions</span>
              </div>
              {!selectedDivision && <Check className="h-4 w-4 text-primary" />}
            </button>

            {/* Divider */}
            {divisions.length > 0 && (
              <div className="border-t border-border my-1" />
            )}

            {/* Division options */}
            {divisions.map((division) => {
              const color = getDivisionColor(division)
              const isSelected = selectedDivision === division._id
              return (
                <button
                  key={division._id}
                  onClick={() => handleSelect(division._id)}
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2.5 text-sm",
                    "transition-colors duration-100",
                    "hover:bg-muted",
                    isSelected && "bg-primary/5"
                  )}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full transition-all"
                    style={{
                      backgroundColor: color,
                      boxShadow: isSelected ? `0 0 0 2px ${color}30` : "none",
                    }}
                  />
                  <div className="flex-1 text-left">
                    <span className="text-foreground font-medium">
                      {division.name}
                    </span>
                    {/* Matter count placeholder - will be populated when API supports it */}
                    {/* <span className="text-xs text-muted-foreground ml-1">
                      · {division.matterCount || 0} active
                    </span> */}
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary" />}
                </button>
              )
            })}
          </div>

        </div>
      )}
    </div>
  )
}
